import {Component, OnInit} from '@angular/core';
import {LoadingState} from 'common';
import {BaseComponent} from '../base.component';
import {AccountAdminService} from '../../services/api/account-admin.service';
import {NotificationService} from '../../../../../common/src/lib/services/notification.service';
import {DeviceAdminService} from '../../services/api/device-admin.service';
import {DeviceAccountInformationExportModel} from '../../viewmodels/DeviceAccountInformationExportModel';
import {AccountAdminDto} from '../../dtos/accountAdmin.dto';
import {ParticipantAdminService} from '../../services/api/participant-admin-service';

@Component({
    selector: 'app-import-csv-patients',
    templateUrl: './import-csv-patients.component.html',
    styleUrls: ['./import-csv-patients.component.scss']
})

export class ImportCsvPatientsComponent extends BaseComponent implements OnInit {
    public loadingState: LoadingState;
    public rows: DeviceAccountInformationExportModel[];

    public uploadedFileName = '';

    public uploadedCsvFile: File | null = null;

    public searchAccountEmailQuery = '';
    public searchResults: AccountAdminDto[] = [];
    public selectedAccountEmail: string | null = null;
    public selectedAccountCode: string | null = null;
    public accountSelected = false;

    public lastImportMessage = '';
    public lastImportSuccess: boolean | null = null;

    // store returned error notes (if any)
    public lastImportErrorNotes: string[] = [];

    // NEW: tag name and color fields (optional)
    public tagName = '';
    public tagColor: string | null = null;
    public tagColorOptions: string[] = ['#F36C6C', '#FE9C52', '#FFC61C', '#86DB68', '#66BEF9', '#AD89E1'];

    constructor(
                private accountAdminService: AccountAdminService,
                private participantAdminService: ParticipantAdminService,
                private deviceAdminService: DeviceAdminService,
                private notificationService: NotificationService) {
        super();
        this.loadingState = LoadingState.LOADING;
    }

    ngOnInit(): void {
        this.analyticsSwitchPage('Admin-ImportCSVPatients', '', '/admin/importCSVPatients');

        this.loadingState = LoadingState.LOADING;
    }

    public onCsvFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input || !input.files || input.files.length === 0) {
            return;
        }

        const file = input.files[0];
        this.uploadedFileName = file.name;

        this.uploadedCsvFile = file;

        this.notificationService.info('CSV file selected and stored for upload: ' + this.uploadedFileName);
    }

    // Call the AccountAdminService to search accounts by email-like query
    public onFindAccounts(): void {
        const query = (this.searchAccountEmailQuery || '').trim();
        if (!query) {
            this.notificationService.info('Please enter an email or fragment to search');
            return;
        }

        // reset previous selection
        this.searchResults = [];
        this.accountSelected = false;
        this.selectedAccountCode = null;
        this.selectedAccountEmail = null;

        this.accountAdminService.accountsByEmailLike(query).subscribe(
            (results: AccountAdminDto[]) => {
                this.searchResults = results || [];
                if (!this.searchResults || this.searchResults.length === 0) {
                    this.notificationService.info('No accounts found');
                }
            },
            (err) => {
                this.notificationService.info('Could not search accounts: ' + (err && err.message ? err.message : String(err)));
            }
        );
    }

    // When the user clicks a search result, mark it as selected and save code
    public onSelectAccount(account: AccountAdminDto): void {
        if (!account) {
            return;
        }
        this.selectedAccountEmail = account.email;
        this.selectedAccountCode = account.code;
        this.accountSelected = true;

        this.searchAccountEmailQuery = account.email;

        this.searchResults = [];
    }

    // Clear the current account selection and reset related fields
    public onClearSelectedAccount(): void {
        this.selectedAccountEmail = null;
        this.selectedAccountCode = null;
        this.accountSelected = false;
        this.searchAccountEmailQuery = '';
        this.searchResults = [];
    }

    // When the user clicks Bulk Import patients, upload the CSV file for the selected account
    public async onBulkImportPatients(): Promise<void> {
        if (!this.accountSelected || !this.selectedAccountCode) {
            this.notificationService.info('Please select an account before importing.');
            return;
        }
        if (!this.uploadedCsvFile) {
            this.notificationService.info('Please select a CSV file before importing.');
            return;
        }

        this.participantAdminService.importCsvPatients(this.selectedAccountCode, this.uploadedCsvFile,
            this.tagName && this.tagName.trim() !== '' ? this.tagName.trim() : undefined, this.tagColor && this.tagColor.trim() !== '' ? this.tagColor.trim() : undefined)
            .subscribe(
                (result) => {
                    const count = result && (result.importedPatients !== undefined) ? result.importedPatients : 0;

                    // capture user info before clearing
                    const userDisplay = this.selectedAccountEmail || this.selectedAccountCode || 'unknown';
                    const usedTagName = this.tagName || '';
                    const usedTagColor = this.tagColor || '';

                    this.lastImportSuccess = true;
                    this.lastImportMessage = `Successfully imported ${count} patients for user ${userDisplay}${usedTagName ?
                        ` with tag '${usedTagName}' (${usedTagColor})` : ''}`;

                    // capture errorNotes from server response (if any)
                    this.lastImportErrorNotes = (result && result.errorNotes) ? result.errorNotes : [];

                    // Reset all fields except the lastImportMessage/lastImportSuccess/lastImportErrorNotes
                    this.onClearSelectedAccount();
                    this.uploadedCsvFile = null;
                    this.uploadedFileName = '';
                    this.tagName = '';
                    this.tagColor = null;
                },
                (error) => {
                    let serverMessage = '';
                    try {
                        if (error && error.error && error.error.message) {
                            serverMessage = String(error.error.message || '');
                        }
                    } catch (e) {
                        serverMessage = '';
                    }

                    if (!serverMessage || serverMessage.trim() === '') {
                        serverMessage = 'Could not complete request, please make sure that your CSV file is valid and you have selected an account';
                    }

                    // capture user info before clearing
                    const userDisplay = this.selectedAccountEmail || this.selectedAccountCode || 'unknown';
                    const usedTagName = this.tagName || '';
                    const usedTagColor = this.tagColor || '';

                    this.lastImportSuccess = false;
                    this.lastImportMessage = `Failed to import patients for user ${userDisplay}: ${serverMessage}` + (usedTagName ? ` (tag '${usedTagName}' ${usedTagColor})` : '');

                    // clear any previous error notes when import failed (server message is shown instead)
                    this.lastImportErrorNotes = [];

                    // Reset all fields except the lastImportMessage/lastImportSuccess
                    this.onClearSelectedAccount();
                    this.uploadedCsvFile = null;
                    this.uploadedFileName = '';
                    // Also clear tag name/color
                    this.tagName = '';
                    this.tagColor = null;
                }
            );
    }

    public selectTagColor(color: string): void {
        if (!this.tagName || this.tagName.trim() === '') {
            // Do not allow selecting a color if tagName is empty
            return;
        }
        this.tagColor = color;
    }

    public onTagNameChange(value: string): void {
        if (!value) {
            this.tagName = '';
            this.clearTagSelectionIfNeeded();
            return;
        }

        // enforce max 30 characters
        if (value.length > 30) {
            this.tagName = value.substring(0, 30);
        } else {
            this.tagName = value;
        }

        this.clearTagSelectionIfNeeded();
    }

    public clearTagSelectionIfNeeded(): void {
        if (!this.tagName || this.tagName.trim() === '') {
            this.tagColor = null;
        }
    }
}
